 'use client';

import { useState, useEffect } from 'react';
import ServiceModal from '../../services/components/ServiceModal';
import Icon from '@/components/ui/AppIcon';
import SERVICES from '../../services/servicesData';

const ServicesPreview = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [hoveredService, setHoveredService] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services = SERVICES.slice(0, 9);

  if (!isHydrated) {
    return (
      <section className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-20">
          <div className="text-center mb-20">
            <div className="h-12 bg-zinc-100 animate-pulse w-64 mx-auto rounded" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2.5 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-black/5 backdrop-blur-xl border border-black/10 mb-4 md:mb-6">
            <div className="w-1.5 h-1.5 bg-black rounded-full" />
            <span className="text-xs font-medium text-black/90 tracking-widest uppercase">
              Our Services
            </span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-black mb-4 md:mb-6 tracking-tight">
            What We Create
          </h2>
          <p className="text-base md:text-lg text-black/60 max-w-2xl mx-auto font-normal px-4">
            Comprehensive design solutions tailored to your vision and lifestyle
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              className="group relative overflow-hidden bg-white rounded-2xl transition-all duration-500 cursor-pointer border border-zinc-200 hover:border-[#1B5E4F]/30 hover:shadow-xl hover:scale-[1.02]"
              onMouseEnter={() => setHoveredService(String(service.id))}
              onMouseLeave={() => setHoveredService(null)}
              onClick={() => {
                setSelectedService(service);
                setIsModalOpen(true);
              }}
            >
              {/* Image */}
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                
                {/* Icon on top left */}
                <div className="absolute top-3 left-3 md:top-4 md:left-4 w-10 h-10 md:w-12 md:h-12 bg-[#1B5E4F] rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name={service.icon as any} size={18} className="text-white md:w-5 md:h-5" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 md:p-6 flex flex-col">
                <div className="mb-3 md:mb-4">
                  <h3 className="font-headline text-lg md:text-xl font-normal text-[#1B5E4F] mb-2 tracking-tight">
                    {service.title}
                  </h3>
                  <p className="text-zinc-600 text-sm font-normal leading-relaxed">
                    {service.description}
                  </p>
                  
                </div>

                {/* Details - shown on hover */}
                <div className="max-h-0 overflow-hidden group-hover:max-h-48 transition-all duration-500 ease-in-out">
                  <div className="pt-4 border-t border-zinc-200 mt-4">
                    <ul className="space-y-2">
                      {(service.features || []).slice(0,4).map((detail, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-zinc-700">
                          <div className="w-1.5 h-1.5 bg-[#1B5E4F] rounded-full mt-1.5 flex-shrink-0" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // use full service object from shared data so modal shows full details
                      setSelectedService(service);
                      setIsModalOpen(true);
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#1B5E4F] hover:underline transition-all"
                  >
                    <span>Learn More</span>
                    <Icon name="ArrowRightIcon" size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 md:mt-16">
          <a
            href="/services"
            className="inline-flex items-center gap-2 md:gap-3 px-8 md:px-10 py-4 md:py-5 bg-black text-white font-medium text-sm md:text-base rounded-full transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-black/20 active:scale-95"
          >
            <span>Explore All Services</span>
            <Icon name="ArrowRightIcon" size={18} />
          </a>
        </div>
      </div>
      <ServiceModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTimeout(() => setSelectedService(null), 300);
        }}
        service={selectedService}
      />
    </section>
  );
};

export default ServicesPreview;